import classNames from 'classnames';
import React from 'react';
import isEqual from 'react-fast-compare';

import {ContextMenuOption} from 'components/ContextMenu';
import {
  getSearchResultPropsForIndex,
  MediocreSearchResultProps,
} from 'notebook/mediocre/search/SearchResultProps';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {
  ColumnConstraint,
  InsertableColumnInfo,
  TableColumnType,
} from 'notebook/tables/constants/TableColumnConstants';
import {Table} from 'notebook/tables/constants/TableConstants';
import {
  SelectionRange,
  TABLE_COLUMN,
  TableDimension,
  TableIndex,
} from 'notebook/tables/constants/TableSelectionConstants';
import TableAxisCell from 'notebook/tables/core-ui/TableAxisCell';
import {TableRowProps} from 'notebook/tables/core-ui/TableRow';
import UnitsTableAxisCell from 'notebook/tables/core-ui/UnitsTableAxisCell';
import {AxisCellModelProvider} from 'notebook/tables/store/contexts/AxisCellModelContext';
import {useTableModel} from 'notebook/tables/store/hooks/useTableModel';
import {computeIsAxisGhostCell} from 'notebook/tables/store/logic/TableDimensionsLogic';
import TableUtil from 'notebook/tables/TableUtil';
import {ElementMouseEvent} from 'util/Types';

export interface TableHeaderRowProps
  // The rendering slice is defined on TableRow; pick it off rather than repeating it here.
  extends Pick<TableRowProps, 'sliceStart'>, MediocreSearchResultProps {
  // Selection state
  // Which columns are fully selected (selection spans all rows). Undefined when
  // no full-column selection exists. This triggers re-renders of column header
  // TableAxisCells when the selection transitions to/from a full-column state,
  // which is necessary for the column header context menu to work correctly.
  selectedColumnRange: TableIndex | undefined;

  // Rendering slice (see TableRow.js)
  sliceEnd: number | null;
  rowHeight?: number | null;

  // Rendering config
  stickyHeaderCells?: boolean; // applies position: sticky to the header cells
  stickyTop?: number; // an offset applied to the sticky header cells

  // Context menu helpers
  getDisallowedColumnNames?: () => Array<string>;
  getExtraContextMenuOptions?: (selectionRange: SelectionRange | null) => Array<ContextMenuOption> | null;

  // Table actions
  axisCellDragOverHandler?: (index: TableIndex, e: ElementMouseEvent) => void;
  axisCellDragStartHandler?: (options: {start: TableIndex; active: string}, e: ElementMouseEvent) => void;
  showDragHandle?: boolean;
  axisCellDragHandleMouseDown?: (dim: TableDimension, index: number) => boolean;
  deleteColumns?: (minIndex: number | null, maxIndex: number | null) => void;
  deleteTable?: () => void;
  finalizeAndExitEditMode?: () => void;
  insertColumnsLeft?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo
  ) => void;
  insertColumnsRight?: (
    minIndex: number | null,
    maxIndex: number | null,
    columnInfo: InsertableColumnInfo
  ) => void;
  renameColumn?: (i: number, name: string) => void;
  setColumnConstraint?: (
    index: number,
    columnConstraint: ColumnConstraint,
    headerRowCell: Partial<Cell>
  ) => void;
  freezeThroughColumn?: (j: number) => void;
  unfreezeFromColumn?: (j: number) => void;
  toggleHeaderColHandler?: () => void;
}

/**
 * The header row in a TableEditable, containing the maximize icon and Excel column labels (A, B, C, ...)
 */
function TableHeaderRow({
  stickyHeaderCells = false,
  sliceStart,
  searchResultsTree,
  selectedSearchResultTree,
  sliceEnd,
  rowHeight,
  stickyTop,
  getDisallowedColumnNames,
  getExtraContextMenuOptions,
  axisCellDragOverHandler,
  axisCellDragStartHandler,
  showDragHandle,
  axisCellDragHandleMouseDown,
  deleteColumns,
  deleteTable,
  finalizeAndExitEditMode,
  insertColumnsLeft,
  insertColumnsRight,
  renameColumn,
  setColumnConstraint,
  freezeThroughColumn,
  unfreezeFromColumn,
  toggleHeaderColHandler,
}: TableHeaderRowProps): JSX.Element | null {
  // TODO(BNCH-166536): Remove selecting the table and instead only select the pieces necessary.
  const {tableModel: table} = useTableModel((model) => model.props.table);
  // Table-level configuration is identical for every column header, so it is read from the table
  // model rather than threaded through props by TableEditable._getHeaderTRProps. Only the
  // `columnHeader` slice is subscribed to so that unrelated model changes do not re-render the
  // header row.
  const {tableModel: columnHeader} = useTableModel((model) => model.columnHeader);
  // The leading cell and the row's own layout are table-level; the per-column consequences of the
  // affixed configuration are derived on each column header's own model.
  const {tableModel: affixed} = useTableModel((model) => model.affixed);
  const {tableModel: firstColumnWidth} = useTableModel((model) => model.measurements.firstColumnWidth);
  const {
    ghostCellsCount,
    locked,
    viewer,
    disableLegacyHeader,
    isLeftAlignColumnHeader,
    isLeftAlignRowHeader,
  } = columnHeader;

  function canModifyCols(): boolean {
    return !viewer && !locked && TableUtil.allowColumnEdits(table);
  }

  function renderCell(j: number): JSX.Element {
    const w = table.table[0].length;
    const isGhostCell = computeIsAxisGhostCell(TABLE_COLUMN, j, w);
    const editable = canModifyCols() && j < w;
    const columnConstraint = table.columnConstraints[j];
    const isEditableLookupColumn =
      !editable &&
      j < w &&
      !viewer &&
      !locked &&
      Boolean(table.allowLookupColumnEdits) &&
      columnConstraint?.columnDefinition?.type === TableColumnType.DEDICATED_LOOKUP;
    const CellComponent = getCellComponent(table, j);

    return (
      <AxisCellModelProvider key={`axis-${j}`} dim={TABLE_COLUMN} index={j}>
        <CellComponent
          {...(editable && {
            delDim: deleteColumns,
            renameDim: renameColumn,
            insertDimBefore: insertColumnsLeft,
            insertDimAfter: insertColumnsRight,
          })}
          {...(isEditableLookupColumn && {
            delDim: deleteColumns,
          })}
          {...(editable && j === 0 && !disableLegacyHeader && {toggleHeader: toggleHeaderColHandler})}
          deleteTable={deleteTable}
          {...(!isGhostCell && {
            cellDragStart: axisCellDragStartHandler,
            cellDragOver: axisCellDragOverHandler,
            showDragHandle,
            dragHandleMouseDown: axisCellDragHandleMouseDown,
          })}
          // Column information
          setColumnConstraint={setColumnConstraint}
          getExtraContextMenuOptions={getExtraContextMenuOptions}
          getDisallowedColumnNames={getDisallowedColumnNames}
          isLeftAlign={isLeftAlignColumnHeader}
          dataTestAttrs={{component: 'TableHeaderRow', element: 'columnLabelCell'}}
          finalizeAndExitEditMode={finalizeAndExitEditMode}
          freezeThroughColumn={freezeThroughColumn}
          unfreezeFromColumn={unfreezeFromColumn}
          {...getSearchResultPropsForIndex({searchResultsTree, selectedSearchResultTree}, j)}
        />
      </AxisCellModelProvider>
    );
  }

  function renderLeadingCell(): JSX.Element | string {
    if (table._rowHeader == null) {
      return (
        <td
          key="left-col-filter"
          className={classNames(
            'mediocre-tableEditable-axisCell mediocre-tableEditable-axisCell--header cell-sticky',
            {
              'align-left': isLeftAlignRowHeader,
              'cell-rightShadow': affixed.showAffixedShadow && affixed.affixedColumnCount === 0,
            }
          )}
        />
      );
    }
    return table._rowHeader;
  }

  const rowStyle = rowHeight != null ? {height: rowHeight} : {};

  return (
    <tr
      className={classNames(
        {'mediocre-tableEditable-headerRow--sticky': stickyHeaderCells},
        {'mediocre-tableEditable-headerRow--noAxis': firstColumnWidth === 0}
      )}
      style={{
        ...rowStyle,
        top: stickyHeaderCells ? stickyTop : undefined,
      }}
    >
      {TableUtil.mapColumns({
        sliceStart,
        sliceEnd,

        ghostCellsCount,
        normalCellsCount: table.table[0].length,

        mapLeadingCell: () => renderLeadingCell(),
        mapNormalCell: (j: number) => renderCell(j),
        mapGhostCell: (j: number) => renderCell(j),
      })}
      {stickyHeaderCells && (
        // When the header row is sticky, the top cells will lose their borders due to how tables
        // collapse borders. These are stand-ins for those.
        <>
          <td className="mediocre-tableEditable-headerRow--sticky-borderTop" />
          <td className="mediocre-tableEditable-headerRow--sticky-borderBottom" />
        </>
      )}
    </tr>
  );
}

export function getCellComponent(table: Table, j: number): typeof TableAxisCell | typeof UnitsTableAxisCell {
  switch (TableUtil.getColumnDefinitionType(table, j)) {
    case TableColumnType.QUANTITY:
    case TableColumnType.CONCENTRATION:
      return UnitsTableAxisCell;
    default:
      return TableAxisCell;
  }
}

export default React.memo(
  TableHeaderRow,
  (prevProps: Record<string, unknown>, nextProps: Record<string, unknown>) =>
    // TODO(BNCH-113857): Switch back to shallow equal once redux is enabled
    Object.keys(nextProps).every((key) => isEqual(prevProps[key], nextProps[key]))
);
